import { currencyFetch } from 'classy-api-proxies/currency'
import { HttpMethod } from 'types/http'
import { getAbortSignal, handleFetchWithAbortException } from 'utils/abortSignal'

export interface CurrencyOption {
  value: string
  label: string
}

interface GetCurrencyConversionProps {
  from: string
  to: string
}

export const getCurrencyConversion = async ({ from, to }: GetCurrencyConversionProps) => {
  /**
   * if the fetch fails, we will want to fallback to the previous currency code aka `from`
   */
  let currencyConversion = {
    oldCurrency: from,
    newCurrency: from,
    date: new Date().toISOString().split('T')[0],
    rate: 1.0,
  }

  if (from === to) {
    return currencyConversion
  }

  try {
    const response = await fetch(`/api/currency/${from}/${to}`, {
      method: HttpMethod.GET,
      // Abort the fetch if it takes longer than 1 second
      signal: getAbortSignal(1000),
    })

    const { rate, date } = await response.json()

    if (rate) {
      currencyConversion = {
        oldCurrency: from,
        newCurrency: to,
        rate,
        date,
      }
    }
  } catch (e) {
    handleFetchWithAbortException(
      e as Error,
      `Unable to fetch currency conversion from ${from} to ${to}`,
    )
  }

  return currencyConversion
}

export const getAvailableCurrencies = async (defaultCurrencyCode: string) => {
  const today = new Date().toISOString().split('T')[0]
  let availableCurrencies = {
    currencies: [defaultCurrencyCode],
    date: today,
  }

  try {
    const response = await currencyFetch(`/currencies/${today}`, {
      method: HttpMethod.GET,
      // Abort the fetch if it takes longer than 1 second
      signal: getAbortSignal(1000),
    })

    const { currencies, date } = await response.json()

    if (currencies.length) {
      availableCurrencies = {
        currencies,
        date,
      }
    }
  } catch (e) {
    handleFetchWithAbortException(e as Error, `Unable to fetch available currencies for ${today}`)
  }

  return availableCurrencies
}

export interface AllCurrenciesDto {
  currency_code: string
  is_disabled: number
  name: string
  precision: number
}

export const getAllCurrencies = async (defaultCurrencyCode: string) => {
  let supportedCurrencies = [
    { currency_code: defaultCurrencyCode, is_disabled: 0, name: defaultCurrencyCode, precision: 2 },
  ]

  try {
    const response = await currencyFetch(
      // Fetch all currencies that are not disabled and have a precision less than 5.
      // This is to prevent fetching crypto currencies (BTC, ETH)
      `/currencies/all?filter=is_disabled!=1&filter=precision<5`,
      {
        method: HttpMethod.GET,
        // Abort the fetch if it takes longer than 1 second
        signal: getAbortSignal(1000),
      },
    )

    const { currencies } = await response.json()

    if (currencies) {
      supportedCurrencies = currencies
    }
  } catch (e) {
    handleFetchWithAbortException(e as Error, `Unable to fetch supported currencies`)
  }

  return supportedCurrencies
}

/**
 * Get currency options for the currency select dropdown
 */
export const getCurrencyOptions = async (currencyCode: string): Promise<CurrencyOption[]> => {
  const supportedCurrencies = await getAllCurrencies(currencyCode)
  const availableCurrencies = await getAvailableCurrencies(currencyCode)
  const currencies = supportedCurrencies
    ?.filter((currency) => availableCurrencies?.currencies.includes(currency.currency_code))
    .map((currency) => ({
      value: currency.currency_code,
      label: `${currency.currency_code} | ${currency.name}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return currencies
}

export const getCurrencyOptionsByCode = async (
  currencyCode?: string,
): Promise<CurrencyOption[]> => {
  try {
    const response = await fetch(`/api/currency${currencyCode ? `?code=${currencyCode}` : ''}`, {
      method: HttpMethod.GET,
      // Abort the fetch if it takes longer than 1 second
      signal: getAbortSignal(1000),
    })

    const currencies: CurrencyOption[] = await response.json()
    return currencies
  } catch (e) {
    handleFetchWithAbortException(
      e as Error,
      `Unable to fetch currency options for ${currencyCode}`,
    )
  }
  return [{ label: currencyCode, value: currencyCode }] as CurrencyOption[]
}
